import {
    TaskEvalResults,
    useStore,
    useStoreUI,
    useTasksStore
} from '@assets/model/Store';
import { observer } from 'mobx-react';
import { Split } from '../Split/Split';
import { Flex } from '../UI/Flex/Flex';
import { TaskChatPanel } from './TaskChatPanel/TaskChatPanel';
import * as style from './TaskPage.module.less';

import icon_checkmark from './icons/checkmark.svg';
import icon_circle from './icons/circle.svg';
import icon_eval_fail from './icons/icon_eval_fail.svg';
import icon_eval_passed from './icons/icon_eval_pass.svg';
import icon_working from './icons/working.svg';

import {
    FlowTaskMessage,
    getFirebase,
    resolveAttachedFileFingerprint
} from '@assets/firebase/FirebaseAdapater';
import { GetIcon } from '../Icons/Icons';
import { cleanFilenameForDisplay } from '@assets/utils/Utils';
import { onValue, ref } from 'firebase/database';
import React, { useEffect, useRef, useState } from 'react';
import { ModalEditEvals } from '../ModalEditEvals/ModalEditEvals';
import { ModalPickFile } from '../ModalPickFile/ModalPickFile';
import { ModalShowEval } from '../ModalShowEval/ModalShowEval';
import { ModalShowVariable } from '../ModalShowVariable/ModalShowVariable';
import { TaskScore } from './TaskScore/TaskScore';
import { Dropdown } from 'react-bootstrap';
import classNames from 'classnames';

interface TaskPageProps {
    onClickBack?: () => void;
    manualSetTaskId?: string;
}

export const TaskPage = observer((props: TaskPageProps) => {
    const { onClickBack, manualSetTaskId } = props;
    const store = useStore();
    const taskId =
        manualSetTaskId || store.selectedTaskId || 'identify_industry';
    const taskStore = useTasksStore();
    const query = store.query;
    const active_task_id = taskId;
    const task = taskStore.get_task_definition(active_task_id);

    const reportId = store.selectedReportId;

    const [storedTasksVars, setStoredVariables] = useState(null);
    const [taskEvalsResults, setTaskEvalResults] = useState<TaskEvalResults[]>(
        []
    );
    const [taskChatHistory, setTaskChatHistory] = useState<any[]>([]);

    const uiStoreForPanel = useStoreUI();
    const panelWidthPercent = uiStoreForPanel.workstreamPanelWidthPercent;
    const splitRef = useRef<HTMLDivElement>(null);
    const [isDraggingPanel, setIsDraggingPanel] = useState(false);

    // Side panel is drag-resizable. Both sides get a floor so the panel can't
    // be dragged narrower than its content or squeeze the chat away.
    const MIN_PANEL_PX = 245;
    const MIN_CHAT_PX = 360;

    const onPanelResizeStart = (event: React.PointerEvent<HTMLDivElement>) => {
        event.preventDefault();
        const bounds = splitRef.current?.getBoundingClientRect();
        if (!bounds || bounds.width <= 0) return;

        setIsDraggingPanel(true);
        event.currentTarget.setPointerCapture(event.pointerId);

        const minPercent = (MIN_PANEL_PX / bounds.width) * 100;
        const maxPercent = 100 - (MIN_CHAT_PX / bounds.width) * 100;

        const onMove = (moveEvent: PointerEvent) => {
            if (minPercent > maxPercent) return;
            const percent =
                ((moveEvent.clientX - bounds.left) / bounds.width) * 100;
            uiStoreForPanel.workstreamPanelWidthPercent = Math.min(
                Math.max(percent, minPercent),
                maxPercent
            );
        };
        const onUp = () => {
            setIsDraggingPanel(false);
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onUp);
        };

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
    };

    const onClickClear = () => {
        const ui = useStoreUI();
        if (ui.chatWaitingForResponse) return;

        const areYouSure = window.confirm(
            'Are you sure you want to clear the chat history? This action cannot be undone.'
        );

        if (!areYouSure) return;

        const fb = getFirebase();
        fb.flowtask_add_clear_job(reportId, taskId);
        // The backend's clear job removes the canonical variable, chat
        // history, task_state and eval results — but doesn't know about
        // the v2 paths (staging proposal, its metadata, file snapshot),
        // so we wipe those here to match the "reset everything" intent.
        if (task?.save_to) {
            fb.flowtask_clear_v2_state(reportId, taskId, task.save_to);
        }
    };

    useEffect(
        function fetchTaskContent() {
            const path = `/flowtasks/vars/${reportId}`;
            const fb = getFirebase();
            const r = ref(fb.db, path);
            const unsub = onValue(r, snapshot => {
                const data = snapshot.val();
                console.log('TaskPage: fetched stored variables:', data);
                setStoredVariables(data);
            });

            return () => {
                unsub();
            };
        },
        [active_task_id]
    );

    useEffect(
        function fetchTaskEvals() {
            const path = `/flowtasks/eval_results/${reportId}/${active_task_id}`;
            const fb = getFirebase();
            const r = ref(fb.db, path);
            const unsub = onValue(r, snapshot => {
                const data = snapshot.val();
                console.log('TaskPage: fetched evals variables:', data);
                setTaskEvalResults(data ?? []);
            });

            return () => {
                unsub();
            };
        },
        [active_task_id]
    );

    useEffect(
        function fetchTaskChatHistory() {
            const path = `/flowtasks/history/${reportId}/${active_task_id}/results`;
            const fb = getFirebase();
            const r = ref(fb.db, path);
            const unsub = onValue(r, snapshot => {
                const data = snapshot.val();
                setTaskChatHistory(Array.isArray(data) ? data : []);
            });

            return () => {
                unsub();
            };
        },
        [active_task_id]
    );

    if (!task) {
        return <div>Task not found: {active_task_id}</div>;
    }

    const tasksState = store.activeReportTasksState;
    const taskState = tasksState.getTaskQuery(active_task_id);

    const active_step = taskState.activeStep;
    const is_complete = taskState.isComplete;
    // Auto-trigger for the comparator now lives at the DealView level
    // (useAutoTriggerComparator) — covers entry, tab visibility, window focus
    // for all modules, not just the currently-open one.

    const var_exists = (storedTasksVars ?? {}).hasOwnProperty(task.save_to);
    const onShowVariable = () => {
        if (!var_exists) return;
        const uiStore = useStoreUI();
        uiStore.modal = {
            title: 'Variable: ' + task.save_to,
            el: (
                <ModalShowVariable
                    variableName={task.save_to}
                    variableValue={storedTasksVars[task.save_to]}
                />
            )
        };
    };

    const sendFilesToFlow = (files: string[]) => {
        const messageToSend: FlowTaskMessage = {
            content: ' ',
            attachedFiles: files,
            // Content fingerprint per file so the comparator can tell apart
            // "same content re-attached" from genuinely new evidence.
            attachedFilesFingerprints: files.map(path =>
                resolveAttachedFileFingerprint(
                    reportId,
                    path,
                    store.allProjectFiles
                )
            )
        };

        // On a human-in-the-loop turn the flow expects an explicit
        // non-approval alongside the new message.
        const lastMessage = taskChatHistory[taskChatHistory.length - 1];
        if (lastMessage?.action != null) {
            messageToSend.approve = false;
        }

        getFirebase().flowtask_add_prompt_job(
            reportId,
            active_task_id,
            messageToSend
        );
    };

    const onClickAddSource = () => {
        const uiStore = useStoreUI();
        if (uiStore.chatWaitingForResponse) return;
        if (taskState.isLocked) return;

        uiStore.modal = {
            title: 'Pick File',
            el: (
                <ModalPickFile
                    onSelectFile={(files: string[]) => {
                        uiStore.modal = null;
                        if (!files?.length) return;
                        sendFilesToFlow(files);
                    }}
                />
            )
        };
    };

    const has_prework = (task.prerequisite_variable?.length ?? 0) > 0;
    const prework_vars = has_prework ? (
        task.prerequisite_variable.map((var_obj, index) => {
            const is_var_ready = !taskState.isLocked;
            var icon = is_var_ready ? icon_checkmark : icon_circle;
            return (
                <Flex alignItems="center" key={'prework_' + index}>
                    <img src={icon} className={style.line_icon_smaller} />
                    <div className={style.line_text} title={var_obj.display}>
                        {var_obj.display}
                    </div>
                </Flex>
            );
        })
    ) : (
        <div className={style.line_text_block}>No pre work items required</div>
    );

    const inner_steps = task.inner_steps_names.map((step_name, index) => {
        const isCurrentStep = index + 1 == Number(active_step);
        const isDone = index + 1 < Number(active_step) || is_complete;

        var icon = null;
        var isSpinning = false;
        if (isCurrentStep && !is_complete) {
            // Active step: spinner while the LLM runs, otherwise the
            // not-started marker (the design has no separate "paused" state).
            if (taskState.status == 'in_progress') {
                icon = icon_working;
                isSpinning = true;
            } else {
                icon = icon_circle;
            }
        } else {
            icon = isDone ? icon_checkmark : icon_circle;
        }

        const isUpcoming = !isDone && !isCurrentStep;
        return (
            <Flex alignItems="center" key={'step_' + index}>
                <img
                    src={icon}
                    className={classNames(style.line_icon_smaller, {
                        [style.spin]: isSpinning
                    })}
                />
                <div
                    className={classNames(style.line_text, {
                        [style.line_text_muted]: isUpcoming
                    })}
                    title={step_name}
                >
                    {step_name}
                </div>
            </Flex>
        );
    });

    const evals = { ...taskStore.global_eval_rules, ...task.evals };
    const eval_list = [];
    const hasEvalResults = (taskEvalsResults?.length ?? 0) > 0;
    var taskScore = -1;
    if (hasEvalResults) {
        taskScore = Math.round(
            (taskEvalsResults.filter(e => e.is_valid).length /
                taskEvalsResults.length) *
                100
        );
    }

    for (const eval_id in evals) {
        const evalItem = evals[eval_id];
        const evalResult = taskEvalsResults.find(e => e.id === eval_id);

        const scoreEl = evalResult ? (
            <img
                src={evalResult.is_valid ? icon_eval_passed : icon_eval_fail}
                className={style.line_icon}
            />
        ) : (
            <div className={style.eval_pending}>–</div>
        );

        const eval_el = (
            <Flex
                direction="row"
                key={'eval_' + eval_id}
                alignItems="center"
                className={style.eval_row}
                style={{ cursor: 'pointer' }}
                onClick={() => {
                    const uiStore = useStoreUI();
                    uiStore.modal = {
                        title: `Evaluation: ${evalItem.name}`,
                        el: (
                            <ModalShowEval
                                reason={
                                    evalResult?.reason ||
                                    'No details available.'
                                }
                                is_valid={evalResult?.is_valid || false}
                            />
                        )
                    };
                }}
            >
                <div className={style.line_text} title={evalItem.name}>
                    {evalItem.name}
                </div>
                {scoreEl}
            </Flex>
        );
        eval_list.push(eval_el);
    }

    // Sources are the files THIS module consumed — the files uploaded in its
    // own chat, not the whole project's Case Files.
    const seenSourceNames = new Set<string>();
    const moduleSources: { name: string; isMd: boolean }[] = [];
    for (const msg of taskChatHistory) {
        const uploads = msg?.fileUploads;
        if (!Array.isArray(uploads)) continue;
        for (const upload of uploads) {
            const name: string = upload?.name;
            if (!name || seenSourceNames.has(name)) continue;
            seenSourceNames.add(name);
            const isMd =
                name.toLowerCase().endsWith('.md') ||
                upload?.mime === 'text/markdown';
            moduleSources.push({ name, isMd });
        }
    }

    const source_list = moduleSources.map((src, index) => {
        const display = cleanFilenameForDisplay(src.name);
        const icon = GetIcon(src.isMd ? 'file-md' : 'file-pdf');
        return (
            <Flex
                alignItems="center"
                key={'source_' + index + '_' + src.name}
                className={style.source_row}
            >
                <img src={icon} className={style.source_icon} />
                <div className={style.line_text} title={display}>
                    {display}
                </div>
            </Flex>
        );
    });

    return (
        <div
            ref={splitRef}
            className={classNames(style.split_wrap, {
                [style.split_dragging]: isDraggingPanel
            })}
        >
            <Split
                spaceBetween={0}
                layoutPercent={[panelWidthPercent, 100 - panelWidthPercent]}
            >
                <Flex direction="column" fillCell={true}>
                    <Flex
                        direction="column"
                        className={style.round_border_container}
                        fillCell={true}
                    >
                        <Flex
                            className={style.back_to_workstreams}
                            direction="row"
                            alignItems="center"
                        >
                            {/* <button
                                className={style.back_button}
                                onClick={() => {
                                    if (onClickBack) onClickBack();
                                }}
                            >
                                <img
                                    src={GetIcon('arrow-left')}
                                    className={style.back_icon}
                                />
                            </button> */}

                            <div className={style.header_title}>
                                {task.name}
                            </div>
                            <Dropdown>
                                <Dropdown.Toggle className="dropdown-toggle-overide">
                                    <i
                                        className={classNames(
                                            'bi bi-three-dots-vertical'
                                        )}
                                        style={{
                                            color: 'white',
                                            fontSize: '14px',
                                            position: 'relative'
                                        }}
                                    ></i>
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item>
                                        <div
                                            className={style.dropdownItem}
                                            onClick={onClickClear}
                                        >
                                            Restart chat & Delete conversation
                                        </div>
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </Flex>

                        <Flex
                            direction="column"
                            className={style.sections_wrap}
                        >
                            {taskState.updatedAt && (
                                <Flex
                                    direction="row"
                                    alignItems="center"
                                    className={style.last_run_line}
                                >
                                    <i className="bi bi-clock-history"></i>
                                    <span>
                                        Last run:{' '}
                                        {new Date(
                                            taskState.updatedAt
                                        ).toLocaleString()}
                                    </span>
                                </Flex>
                            )}

                            <Flex
                                direction="column"
                                className={style.prework_list}
                            >
                                <div className={style.inner_title}>
                                    Pre-work items
                                </div>
                                {prework_vars}
                            </Flex>

                            <Flex
                                direction="column"
                                className={style.steps_list}
                            >
                                <div className={style.inner_title}>
                                    Work items
                                </div>
                                {inner_steps}
                            </Flex>
                            {Object.keys(evals).length > 0 && (
                                <Flex
                                    direction="column"
                                    className={style.steps_list_eval}
                                >
                                    <Flex
                                        direction="row"
                                        alignItems="center"
                                        className={style.inner_title}
                                    >
                                        <div style={{ flexGrow: 1 }}>
                                            Evaluations
                                        </div>
                                        <div
                                            className="hover-button"
                                            onClick={() => {
                                                const ui = useStoreUI();
                                                ui.modal = {
                                                    title: 'Edit Evals',
                                                    el: (
                                                        <ModalEditEvals
                                                            taskId={
                                                                active_task_id
                                                            }
                                                        />
                                                    )
                                                };
                                            }}
                                        >
                                            <img
                                                src={GetIcon('edit-04')}
                                                className={
                                                    style.header_action_icon
                                                }
                                            />
                                        </div>
                                    </Flex>
                                    {eval_list}
                                    <Flex
                                        direction="row"
                                        alignItems="center"
                                        className={style.eval_footer}
                                    >
                                        <div className={style.eval_total_label}>
                                            Total Score
                                        </div>
                                        <div style={{ flexGrow: 1 }}></div>
                                        {hasEvalResults ? (
                                            <div
                                                className={style.evalPercentBox}
                                            >
                                                <TaskScore score={taskScore} />
                                            </div>
                                        ) : (
                                            <div
                                                className={
                                                    style.eval_not_started
                                                }
                                            >
                                                Not Started
                                            </div>
                                        )}
                                    </Flex>
                                </Flex>
                            )}

                            <Flex
                                direction="column"
                                className={style.sources_list}
                            >
                                <Flex
                                    direction="row"
                                    alignItems="center"
                                    className={style.inner_title}
                                >
                                    <div style={{ flexGrow: 1 }}>Sources</div>
                                    <img
                                        src={GetIcon('plus')}
                                        className={style.header_action_icon}
                                        onClick={onClickAddSource}
                                    />
                                </Flex>
                                {source_list.length > 0 ? (
                                    source_list
                                ) : (
                                    <div className={style.sources_empty}>
                                        Attach case files as context
                                    </div>
                                )}
                            </Flex>
                        </Flex>

                        <div className={style.saved_outputs}>
                            <Flex alignItems="center">
                                <div
                                    className={style.is_var_saved}
                                    onClick={() => {
                                        onShowVariable();
                                    }}
                                >
                                    {var_exists ? '1' : '0'}
                                </div>
                                <div style={{ flexGrow: 1 }}></div>
                                <div className={style.saved_outputs_label}>
                                    Saved Outputs
                                </div>
                            </Flex>
                        </div>
                    </Flex>
                </Flex>

                <TaskChatPanel
                    taskId={active_task_id}
                    isLocked={taskState.isLocked}
                    isVarSaved={var_exists}
                />
            </Split>

            <div
                className={style.panel_resize_handle}
                style={{ left: `calc(${panelWidthPercent}% - 6px)` }}
                onPointerDown={onPanelResizeStart}
                role="separator"
                aria-orientation="vertical"
            />
        </div>
    );
});
